import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Button,
  FormControl,
  FormHelperText,
  Icon,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Typography,
  TextField
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Formik } from 'formik';
import * as yup from 'yup';
import { userSignUpOneLoginApi } from 'api';


const useStyles = makeStyles({
  form: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px'
    },
    '& .MuiButton-root': {
      borderRadius: '57px'
    },
    '& .MuiInputLabel-formControl': {
      left: '30px'
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      transform: 'translate(-20px, -6px) scale(0.75)'
    }
  },
  icon: {
    color: '#828282'
  }
});

const SignUpForm = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [showPassword, toggleShowPassword] = useState(false);
  const [shrink, setShrink] = useState({
    firstName: false,
    lastName: false,
    email: false,
    password: false
  });

  return (
    <>
      <Typography variant="h2" className="h1 h-text-gray font-bold py-2 my-15">Welcome</Typography>
      <Formik
        onSubmit={(values) => userSignUpOneLoginApi({dispatch, userData: values})}
        initialValues={{
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          couponCode: ''
        }}
        validationSchema={yup.object({
          firstName: yup
            .string()
            .required('Field is required'),
          lastName: yup
            .string()
            .required('Field is required'),
          email: yup
            .string()
            .email('Please enter valid email address')
            .required('Field is required'),
          password: yup
            .string()
            .matches(
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/,
              'Must contain at least 8 characters including at least 1 uppercase, 1 lowercase, 1 number and 1 special character'
            )
            .required('Field is required'),
          couponCode: yup
            .string()
            .required('Field is required'),
        })}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit
        }) => (
          <form onSubmit={handleSubmit} className={`w-full max-w-500 px-4 ${classes.form}`}>
            <FormControl variant="outlined" fullWidth className="mb-5">
              <InputLabel
                required
                htmlFor="firstName"
                className={`${(touched.firstName && Boolean(errors.firstName)) && 'text-error'}`}
                shrink={shrink.firstName}
              >
                First Name
              </InputLabel>
              <OutlinedInput
                name="firstName"
                autoComplete="no-name"
                value={values.firstName}
                onChange={handleChange}
                onBlur={(event) => {
                  handleBlur(event);
                  !event.target.value && setShrink({...shrink, firstName: false});
                }}
                onFocus={() => setShrink({...shrink, firstName: true})}
                startAdornment={
                  <InputAdornment position="start">
                    <Icon className={classes.icon} fontSize="small">perm_identity_outlined</Icon>
                  </InputAdornment>
                }
                labelWidth={shrink.firstName ? 70 : 0}
                error={touched.firstName && Boolean(errors.firstName)}
              />
              {(touched.firstName && Boolean(errors.firstName)) &&
              <FormHelperText className="text-error">{errors.firstName}</FormHelperText>
              }
            </FormControl>
            <FormControl variant="outlined" fullWidth className="mb-5">
              <InputLabel
                required
                htmlFor="lastName"
                className={`${(touched.lastName && Boolean(errors.lastName)) && 'text-error'}`}
                shrink={shrink.lastName}
              >
                Last Name
              </InputLabel>
              <OutlinedInput
                name="lastName"
                autoComplete="no-name"
                value={values.lastName}
                onChange={handleChange}
                onBlur={(event) => {
                  handleBlur(event);
                  !event.target.value && setShrink({...shrink, lastName: false});
                }}
                onFocus={() => setShrink({...shrink, lastName: true})}
                startAdornment={
                  <InputAdornment position="start">
                    <Icon className={classes.icon} fontSize="small">perm_identity_outlined</Icon>
                  </InputAdornment>
                }
                labelWidth={shrink.lastName ? 70 : 0}
                error={touched.lastName && Boolean(errors.lastName)}
              />
              {(touched.lastName && Boolean(errors.lastName)) &&
              <FormHelperText className="text-error">{errors.lastName}</FormHelperText>
              }
            </FormControl>
            <FormControl variant="outlined" fullWidth className="mb-5">
              <InputLabel
                required
                htmlFor="email"
                className={`${(touched.email && Boolean(errors.email)) && 'text-error'}`}
                shrink={shrink.email}
              >
                Email
              </InputLabel>
              <OutlinedInput
                name="email"
                autoComplete="no-email"
                value={values.email}
                onChange={handleChange}
                onBlur={(event) => {
                  handleBlur(event);
                  !event.target.value && setShrink({...shrink, email: false});
                }}
                onFocus={() => setShrink({...shrink, email: true})}
                startAdornment={
                  <InputAdornment position="start">
                    <Icon className={classes.icon} fontSize="small">mail_outlined_outlined</Icon>
                  </InputAdornment>
                }
                labelWidth={shrink.email ? 40 : 0}
                error={touched.email && Boolean(errors.email)}
              />
              {(touched.email && Boolean(errors.email)) &&
                <FormHelperText className="text-error">{errors.email}</FormHelperText>
              }
            </FormControl>
            <FormControl variant="outlined" fullWidth className="mb-5">
              <InputLabel
                required
                htmlFor="password"
                className={`${(touched.password && Boolean(errors.password)) && 'text-error'}`}
                shrink={shrink.password}
              >
                Password
              </InputLabel>
              <OutlinedInput
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={values.password}
                onChange={handleChange}
                onBlur={(event) => {
                  handleBlur(event);
                  !event.target.value && setShrink({...shrink, password: false});
                }}
                onFocus={() => setShrink({...shrink, password: true})}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => toggleShowPassword(!showPassword)}
                      onMouseDown={(event) => event.preventDefault()}
                      edge="end"
                    >
                      {showPassword
                        ? <Icon className={classes.icon} fontSize="small">visibility_outlined</Icon>
                        : <Icon className={classes.icon} fontSize="small">visibility_off_outlined</Icon>
                      }
                    </IconButton>
                  </InputAdornment>
                }
                startAdornment={
                  <InputAdornment position="start">
                    <Icon className={classes.icon} fontSize="small">lock_outlined</Icon>
                  </InputAdornment>
                }
                labelWidth={shrink.password ? 70 : 0}
                error={touched.password && Boolean(errors.password)}
                inputProps={{
                  autoComplete: 'new-password',
                  form: {
                    autocomplete: 'off',
                  },
                }}
              />
              {(touched.password && Boolean(errors.password)) &&
                <FormHelperText className="text-error">{errors.password}</FormHelperText>
              }
            </FormControl>
            <FormControl variant="outlined" fullWidth className="mb-5">
              <TextField
                fullWidth
                required
                variant="outlined"
                className="bg-white"
                name="couponCode"
                label="Access Code"
                value={values.couponCode}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched?.couponCode && Boolean(errors?.couponCode)}
                helperText={touched?.couponCode && errors?.couponCode}
              />
            </FormControl>
            <Button
              color="primary"
              variant="contained"
              type="submit"
              className="w-full my-5"
            >
              Sign Up
            </Button>
            <Typography variant="body1" className="text-center mb-15">
              Already have an account?{' '}
              <Link to="/login">
                <strong>Sign In</strong>
              </Link>
            </Typography>
          </form>
        )}
      </Formik>
    </>
  );
};

export default SignUpForm;
